import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { AuditType, RefundAuditInfoData } from '../../api';
import { formatRefundCardSuffix, useRefundAmountFormatter } from '../../refunds/refundFormat';

type RefundAuditDetailsProps = {
  refund: RefundAuditInfoData;
  auditType: AuditType;
};

const BACKGROUND_BY_AUDIT_TYPE: Partial<Record<AuditType, string>> = {
  [AuditType.REFUND_SUCCEEDED]: 'primary.light',
  [AuditType.REFUND_PENDING]: 'secondary.light',
  [AuditType.REFUND_FAILED]: 'error.light',
  [AuditType.REFUND_BLOCKED]: 'error.light',
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Grid container alignItems="baseline" gap={0.5}>
    <Typography variant="caption" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Grid>
);

export const RefundAuditDetails = ({ refund, auditType }: RefundAuditDetailsProps) => {
  const { t } = useTranslation();
  const formatRefundAmount = useRefundAmountFormatter();

  const amount = refund.amount != null ? formatRefundAmount(refund.amount) : '';
  const payment = [
    refund.entity_type ? t(`refunds.entity.${refund.entity_type}`) : '',
    formatRefundCardSuffix(refund.card_suffix),
  ]
    .filter(Boolean)
    .join(' ');
  const error = [refund.error_code, refund.error_message].filter(Boolean).join(' ');

  return (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        backgroundColor: BACKGROUND_BY_AUDIT_TYPE[auditType],
        maxWidth: 480,
      }}>
      <Grid container flexDirection="column" gap={0.25}>
        {amount && <DetailRow label={t('auditlog.refund.amount')} value={amount} />}
        {payment && <DetailRow label={t('auditlog.refund.payment')} value={payment} />}
        {refund.reason_label && <DetailRow label={t('auditlog.refund.reason')} value={refund.reason_label} />}
        {error && <DetailRow label={t('auditlog.refund.error')} value={error} />}
        {refund.block_reason && (
          <DetailRow
            label={t('auditlog.refund.blockReason')}
            value={t(`auditlog.refund.block_reasons.${refund.block_reason}`)}
          />
        )}
      </Grid>
    </Box>
  );
};
